import { useEffect } from 'react';
import { NavigationControls } from './Navigation';
import { useAbyssViewerContext } from './AbyssViewerContext';

export type SelectBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const getRgbaFromNumber = (
  color: number | undefined
): { r: number; g: number; b: number; a: number } => {
  if (color) {
    const r = (color >> 24) & 0xff;
    const g = (color >> 16) & 0xff;
    const b = (color >> 8) & 0xff;
    const a = color & 0xff;
    return { r, g, b, a };
  }
  return { r: 255, g: 255, b: 255, a: 255 };
};

export const useSelectBoxControls = (
  navigationControls: NavigationControls | undefined,
  domElement: HTMLCanvasElement,
  onSelectionDone: (selectBox: SelectBox, mouseEvent: PointerEvent) => void,
  selectionColors: { shiftKeyColor: number; altKeyColor: number } | undefined,
  isEnabled = true
) => {
  const { drawBoxReference: drawBox } = useAbyssViewerContext();
  useEffect(() => {
    let startPoint: { x: number; y: number } | undefined;
    let endPoint: { x: number; y: number } | undefined;
    let selectionBoxActive: boolean;

    const activateSelectionBox = () => {
      navigationControls?.setIsEnabled(false);
      startPoint = undefined;
      endPoint = undefined;
      selectionBoxActive = true;
    };

    const deactivateSelectionBox = () => {
      navigationControls?.setIsEnabled(true);
      startPoint = undefined;
      endPoint = undefined;
      selectionBoxActive = false;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (selectionBoxActive) {
        // Block key down handler from repeatedly firing, just need it once until keyUp
        return;
      }
      drawBox?.current?.setAttribute('style', '');
      const { shiftKey, altKey } = event;
      if (shiftKey || altKey) {
        activateSelectionBox();
      }
    };

    const onKeyUp = () => {
      drawBox?.current?.setAttribute('style', '');
      if (selectionBoxActive) {
        deactivateSelectionBox();
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!selectionBoxActive) return;
      const { clientX, clientY } = event;
      startPoint = { x: clientX, y: clientY };
      drawBox?.current?.setAttribute('style', '');
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!selectionBoxActive) return;
      const { clientX, clientY } = event;
      endPoint = { x: clientX, y: clientY };
      if (startPoint && endPoint) {
        const { altKey } = event;
        const color = altKey
          ? getRgbaFromNumber(selectionColors?.altKeyColor)
          : getRgbaFromNumber(selectionColors?.shiftKeyColor);

        const topOffset = domElement?.getBoundingClientRect().y || 0;
        const leftOffset = domElement?.getBoundingClientRect().x || 0;
        drawBox?.current?.setAttribute(
          'style',
          `position: absolute;
          z-index: 999;
          pointer-events: none;
          left: ${Math.min(startPoint.x - leftOffset, endPoint.x - leftOffset)}px;
          top: ${Math.min(startPoint.y - topOffset, endPoint.y - topOffset)}px;
          width: ${Math.abs(endPoint.x - startPoint.x)}px;
          height: ${Math.abs(endPoint.y - startPoint.y)}px;
          border-style: dashed;
          border-color: rgba(${color.r}, ${color.g}, ${color.b}, ${1} );
          background-color: rgba(${color.r}, ${color.g}, ${color.b}, ${0.3} );`
        );
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!selectionBoxActive) return;
      if (!startPoint?.x) return;
      if (!startPoint?.y) return;
      if (!endPoint?.x) return;
      if (!endPoint?.y) return;

      const selectBox: SelectBox = {
        x: Math.min(startPoint.x, endPoint.x),
        y: Math.min(startPoint.y, endPoint.y),
        width: Math.abs(endPoint.x - startPoint.x),
        height: Math.abs(endPoint.y - startPoint.y),
      };

      onSelectionDone(selectBox, event);

      drawBox?.current?.setAttribute('style', '');
      startPoint = undefined;
      endPoint = undefined;
    };

    if (isEnabled) {
      window.addEventListener('blur', deactivateSelectionBox);
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);
      domElement.addEventListener('pointerdown', onPointerDown);
      domElement.addEventListener('pointermove', onPointerMove);
      domElement.addEventListener('pointerup', onPointerUp);

      return () => {
        window.removeEventListener('blur', deactivateSelectionBox);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        domElement.removeEventListener('pointerdown', onPointerDown);
        domElement.removeEventListener('pointermove', onPointerMove);
        domElement.removeEventListener('pointerup', onPointerUp);
      };
    }

    return () => {};
  }, [
    isEnabled,
    domElement,
    drawBox,
    navigationControls,
    onSelectionDone,
    selectionColors?.altKeyColor,
    selectionColors?.shiftKeyColor,
  ]);
};
